import React, { useMemo } from 'react';
import { CartItem } from '@src/stories/ecommerce/Cart/CartItems/CartItem';
import { ICartItem } from '@src/types/cart';
import { useResponsiveBreakpoints } from '../../../../hooks/useMediaQuery';

interface MinicartItemsProps {
  items?: ICartItem[];
  block?: any;
  globals?: any;
  onQuantityChange?: (item: ICartItem, newQuantity: number) => void;
  onRemoveItem?: (item: ICartItem) => void;
  disableUndoPopup?: boolean; // New prop to control undo popup behavior
  settings?: {
    // Display options
    show_vendor?: boolean;
    show_price?: boolean;
    show_product_options?: boolean;
    show_line_discounts?: boolean;

    // Text styling
    vendor_text_style?: string;
    vendor_text_style_mobile?: string;
    vendor_text_style_tablet?: string;
    vendor_text_style_ultrawide?: string;
    vendor_text_style_enable_responsive?: boolean;
    vendor_text_color?: string;
    product_title_style?: string;
    product_title_style_mobile?: string;
    product_title_style_tablet?: string;
    product_title_style_ultrawide?: string;
    product_title_style_enable_responsive?: boolean;
    product_title_text_color?: string;
    compare_price_style?: string;
    compare_price_style_mobile?: string;
    compare_price_style_tablet?: string;
    compare_price_style_ultrawide?: string;
    compare_price_style_enable_responsive?: boolean;
    compare_price_text_color?: string;
    final_price_style?: string;
    final_price_style_mobile?: string;
    final_price_style_tablet?: string;
    final_price_style_ultrawide?: string;
    final_price_style_enable_responsive?: boolean;
    final_price_text_color?: string;
    product_option_style?: string;
    product_option_style_mobile?: string;
    product_option_style_tablet?: string;
    product_option_style_ultrawide?: string;
    product_option_style_enable_responsive?: boolean;
    product_option_text_color?: string;
    discount_text_style?: string;
    discount_text_style_mobile?: string;
    discount_text_style_tablet?: string;
    discount_text_style_ultrawide?: string;
    discount_text_style_enable_responsive?: boolean;
    discount_text_color?: string;

    // Appearance settings
    color_schema?: string;
    border_radius?: number;
    show_item_border?: boolean;
    item_border_color?: string;
    item_border_width?: number;

    // Layout & Spacing settings
    direction?: string;
    item_gap?: number;
    padding_top?: number;
    padding_top_enable_responsive?: boolean;
    padding_top_mobile?: number;
    padding_top_tablet?: number;
    padding_top_ultrawide?: number;
    padding_bottom?: number;
    padding_bottom_enable_responsive?: boolean;
    padding_bottom_mobile?: number;
    padding_bottom_tablet?: number;
    padding_bottom_ultrawide?: number;
    padding_left_right?: number;
    padding_left_right_enable_responsive?: boolean;
    padding_left_right_mobile?: number;
    padding_left_right_tablet?: number;
    padding_left_right_ultrawide?: number;
    margin_top?: number;
    margin_bottom?: number;
    margin_left_right?: number;
    max_height?: number;

    // Divider settings
    show_divider?: boolean;
    divider_color?: string;
    divider_thickness?: number;
  };
}

export const MinicartItems = ({
  items = [],
  settings = {},
  block,
  globals,
  onQuantityChange,
  onRemoveItem
}: MinicartItemsProps) => {
  const { isMobile, isTablet, isUltrawide } = useResponsiveBreakpoints();

  // Helper function to get responsive padding values
  const getResponsivePadding = (
    basePadding: number,
    enableResponsive: boolean,
    mobilePadding?: number,
    tabletPadding?: number,
    ultrawidePadding?: number
  ) => {
    if (!enableResponsive) return basePadding;

    if (isMobile && mobilePadding !== undefined) return mobilePadding;
    if (isTablet && tabletPadding !== undefined) return tabletPadding;
    if (isUltrawide && ultrawidePadding !== undefined) return ultrawidePadding;

    return basePadding;
  };

  // Layout & Spacing with responsive support
  const direction = settings?.direction || 'column';
  const itemGap = settings?.item_gap ?? 0;
  
  // Calculate responsive padding values
  const paddingTop = getResponsivePadding(
    settings?.padding_top ?? 0,
    settings?.padding_top_enable_responsive ?? false,
    settings?.padding_top_mobile,
    settings?.padding_top_tablet,
    settings?.padding_top_ultrawide
  );
  
  const paddingBottom = getResponsivePadding(
    settings?.padding_bottom ?? 0,
    settings?.padding_bottom_enable_responsive ?? false,
    settings?.padding_bottom_mobile,
    settings?.padding_bottom_tablet,
    settings?.padding_bottom_ultrawide
  );
  
  const paddingLeftRight = getResponsivePadding(
    settings?.padding_left_right ?? 0,
    settings?.padding_left_right_enable_responsive ?? false,
    settings?.padding_left_right_mobile,
    settings?.padding_left_right_tablet,
    settings?.padding_left_right_ultrawide
  );
  
  const marginTop = settings?.margin_top ?? 0;
  const marginBottom = settings?.margin_bottom ?? 0;
  const marginLeftRight = settings?.margin_left_right ?? 0;

  // Appearance
  const borderRadius = settings?.border_radius || 0;

  // Divider settings
  const showDivider = settings?.show_divider || false;
  const dividerColor = settings?.divider_color || '#e5e7eb';
  const dividerThickness = settings?.divider_thickness || 1;

  // Memoize container style
  const containerStyle = useMemo(
    () =>
      ({
        marginTop: `${marginTop}px`,
        marginBottom: `${marginBottom}px`,
        marginLeft: `${marginLeftRight}px`,
        marginRight: `${marginLeftRight}px`,
        paddingTop: `${paddingTop}px`,
        paddingBottom: `${paddingBottom}px`,
        paddingLeft: `${paddingLeftRight}px`,
        paddingRight: `${paddingLeftRight}px`,
        borderRadius: `${borderRadius}px`
      } as React.CSSProperties),
    [marginTop, marginBottom, marginLeftRight, paddingTop, paddingBottom, paddingLeftRight, borderRadius]
  );

  const itemsStyle = useMemo(() => {
    return {
      display: 'flex',
      flexDirection: 'column',
      gap: `${itemGap}px`
    } as React.CSSProperties;
  }, [direction, itemGap]);

  // Get the correct color schema from block data
  const colorSchema = block?.color_schema || settings?.color_schema;

  return (
    <div
      className={`minicart-items ${colorSchema ? `color-${colorSchema}` : ''}`}
      style={containerStyle}
    >
      <ul
        className='minicart-items__list list-none p-0 w-full'
        style={itemsStyle}
      >
        {items.map((item, index) => (
          <CartItem
            key={item.key || index}
            item={item}
            settings={{ ...settings, globals }}
            index={index}
            onQuantityChange={onQuantityChange}
            onRemoveItem={onRemoveItem}
            disableUndoPopup={true}
            isFirstItem={index === 0}
            isLastItem={index === items.length - 1}
          />
        ))}
      </ul>

      {showDivider && (
        <div
          style={{
            width: '100%',
            height: dividerThickness,
            backgroundColor: dividerColor
          }}
        />
      )}
    </div>
  );
};
